import { Link } from 'react-router';
import { usePortalAuth } from '@/portal/auth/hooks';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ChevronDown,
  FileCheck,
  LogOut,
  Moon,
  Monitor,
  Sun,
  User,
} from 'lucide-react';
import { usePortalAccess } from '../hooks/usePortalAccess';
import { useMe } from '../hooks/useMe';
import { useTheme } from '@/hooks/useTheme';
import type { Theme } from '@/contexts/theme-types';

const NEXT_THEME: Record<Theme, Theme> = {
  light: 'dark',
  dark: 'system',
  system: 'light',
};

function initialsOf(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const [first] = parts;
  const last = parts[parts.length - 1];
  return `${first[0]}${parts.length > 1 ? last[0] : ''}`.toUpperCase();
}

export function PortalUserMenu() {
  const { logout } = usePortalAuth();
  const { canAccess } = usePortalAccess();
  const { data: me } = useMe();
  const { theme, setTheme } = useTheme();

  const displayName = me?.display_name ?? 'User';
  const initials = me ? initialsOf(displayName) : '?';

  // Cycle through themes: light -> dark -> system -> light
  const cycleTheme = () => {
    setTheme(NEXT_THEME[theme]);
  };

  const getThemeIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun className="h-4 w-4" />;
      case 'dark':
        return <Moon className="h-4 w-4" />;
      case 'system':
        return <Monitor className="h-4 w-4" />;
    }
  };

  const getThemeLabel = () => {
    switch (theme) {
      case 'light':
        return 'Light';
      case 'dark':
        return 'Dark';
      case 'system':
        return 'System';
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-accent transition-colors">
          <div className="hidden sm:block text-right">
            <p className="text-sm font-medium text-foreground">{displayName}</p>
            <p className="text-xs text-muted-foreground">Investor Portal</p>
          </div>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-sm font-medium text-white">
            {initials}
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuItem asChild>
          <Link
            to="/portal/profile"
            className="flex cursor-pointer items-center gap-2"
          >
            <User className="h-4 w-4" />
            Profile
          </Link>
        </DropdownMenuItem>

        {canAccess('change_data') && (
          <DropdownMenuItem asChild>
            <Link
              to="/portal/change-data"
              className="flex cursor-pointer items-center gap-2"
            >
              <FileCheck className="h-4 w-4" />
              Change Data
            </Link>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={cycleTheme}
          className="flex cursor-pointer items-center justify-between"
        >
          <span className="flex items-center gap-2">
            {getThemeIcon()}
            Theme
          </span>
          <span className="text-xs text-muted-foreground">
            {getThemeLabel()}
          </span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="flex items-center gap-2">
          <LogOut className="h-4 w-4" />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
